// store/actions/friendActions.js
import CustomerService from '@/services/userServer';
import Constant from "@/common/Constant.js";
import { EventBus } from '@/common/EventBus';
import SessionCache from '@/common/Session';

export default {
    async getFriends({ commit, dispatch, state }, params = {}) {
        const { callback, page = 1, pageSize = 20, append = false, resetPagination = false } = params;

        if (!state.isLogin) {
            return [];
        }

        if (resetPagination) {
            commit('SET_FRIENDS_PAGINATION', { currentPage: 1, hasMore: true, loading: false });
            commit('SET_ALL_FRIENDS_LOADED', false);
        }

        if (append && state.allFriendsLoaded) {
            if (callback) callback([]);
            return [];
        }

        commit('SET_FRIENDS_PAGINATION', { loading: true });

        try {

            const res = await CustomerService.getCustomerAgents({
                page: page,
                itemsPerPage: pageSize,
                PcChat: true,
                headCompanyGuid: '1c344d4cd44d44af90a4010688c5fc96'
            });

            let friendsList = [];
            if (res && res.code === 0) {
                if (res.data?.records) {
                    friendsList = res.data.records;
                } else if (Array.isArray(res.data)) {
                    friendsList = res.data;
                }
            }

            const convertedFriends = (friendsList || []).map(friend => ({
                agentAccountGuid: friend.agentAccountGuid || friend.accountGuid,
                agentName: friend.agentName || friend.name || '未知',
                agentAvatar: friend.agentAvatar || friend.avatar || Constant.DEFAULT_AVATAR,
                nickName: friend.nickName || friend.name || '未知',
                agentMobile: friend.agentMobile || friend.mobile || '',
                agentGuid: friend.agentGuid || friend.guid || '',
                roles: friend.roles || 'agent',
                lastMsg: friend.lastMsg || '',
                lastTime: friend.lastTime || 0,
                unreadCount: friend.unreadCount || 0
            }));

            const uniqueMap = new Map();
            convertedFriends.forEach(friend => {
                if (friend?.agentAccountGuid) uniqueMap.set(friend.agentAccountGuid, friend);
            });
            const uniqueConvertedFriends = Array.from(uniqueMap.values());

            if (append) {
                const currentFriendsMap = new Map();
                if (state.friendslist) {
                    state.friendslist.forEach(f => {
                        if (f?.agentAccountGuid) currentFriendsMap.set(f.agentAccountGuid, f);
                    });
                }
                uniqueConvertedFriends.forEach(f => currentFriendsMap.set(f.agentAccountGuid, f));
                const mergedFriendsList = Array.from(currentFriendsMap.values());
                commit('SET_FRIENDS_LIST', mergedFriendsList);
                const hasMore = friendsList.length >= pageSize;
                commit('SET_FRIENDS_PAGINATION', { currentPage: page, hasMore, loading: false });
                if (!hasMore) commit('SET_ALL_FRIENDS_LOADED', true);
                await dispatch('processFriendList', { friendsList: mergedFriendsList });
                return mergedFriendsList;
            } else {
                commit('SET_FRIENDS_LIST', uniqueConvertedFriends);
                commit('SET_FRIENDS_PAGINATION', {
                    currentPage: 1, pageSize, total: friendsList.length,
                    hasMore: friendsList.length >= pageSize, loading: false
                });
                await dispatch('processFriendList', { friendsList: uniqueConvertedFriends });
                return uniqueConvertedFriends;
            }
        } catch (error) {
            console.error('Get friends error:', error);
            if (!append) commit('SET_FRIENDS_LIST', []);
            commit('SET_FRIENDS_PAGINATION', { loading: false });
            if (callback) callback([]);
            return [];
        }
    },

    async ensureFriendAndChat({ commit, dispatch, state }, agentInfo) {
        try {
            const friendGuid = agentInfo.agentAccountGuid;
            if (!friendGuid) return false;

            const token = SessionCache.getAccessToken();
            const customerInfo = SessionCache.getCustomerInfo();

            if (!token || !customerInfo?.account?.guid) {
                commit('updateState', { data: true, key: "showLogin" }, { root: true });
                return false;
            }

            if (!state.isLogin) {
                await dispatch('initIM', {
                    account: customerInfo.account.guid,
                    token: token,
                    callbackv2: () => {
                        // 空回调 - IM 初始化完成后的回调
                    }
                });
                await new Promise(resolve => {
                    let attempts = 0;
                    const checkLogin = setInterval(() => {
                        attempts++;
                        if (state.isLogin || attempts >= 20) {
                            clearInterval(checkLogin);
                            resolve();
                        }
                    }, 300);
                });
            }

            if (!state.friendslist?.length) {
                await dispatch('getFriends', { page: 1, pageSize: 20, append: false, resetPagination: true });
            }

            const isFriend = state.friendslist?.some(f => f?.agentAccountGuid === friendGuid);
            if (!isFriend) {
                const addResult = await dispatch('addFriend', agentInfo);
                if (!addResult) return false;
            }

            await dispatch('forceRefreshConversations');
            try {
                EventBus.$emit('refrenshSessionList', true);
                EventBus.$emit('force-refresh-conversations', { timestamp: Date.now() });
            } catch (e) {
                // 静默失败，不影响主流程
                console.warn('[IM] 触发刷新事件失败:', e);
            }
            return true;
        } catch (error) {
            console.error('ensureFriendAndChat 执行失败:', error);
            return false;
        }
    },

    async processFriendList({ commit, dispatch, state }, params = {}) {
        const { callback, friendsList } = params;
        const list = friendsList || state.friendslist;

        if (!list?.length) {
            commit('SET_FRIENDS_LIST', []);
            if (callback) callback();
            return;
        }

        const uniqueMap = new Map();
        list.forEach(friend => {
            if (friend?.agentAccountGuid) uniqueMap.set(friend.agentAccountGuid, friend);
        });
        const uniqueList = Array.from(uniqueMap.values());

        const friendsGuid = [];
        for (const friend of uniqueList) {
            if (!friend?.agentAccountGuid) continue;
            friendsGuid.push(friend.agentAccountGuid);
            if (!state.sessionMap.has(friend.agentAccountGuid)) {
                await dispatch('initSession', { sessionId: friend.agentAccountGuid, friendInfo: friend });
            }
        }

        commit('SET_FRIENDS_LIST', uniqueList);
        commit('SET_FRIENDS_GUID', friendsGuid);
        try {
            EventBus.$emit('refrenshSessionList', true);
        } catch (e) {
            // 静默失败，不影响主流程
            console.warn('[IM] 触发刷新事件失败:', e);
        }
        if (callback) callback();
    },

    async initSession({ commit, state }, { sessionId, friendInfo }) {
        if (!sessionId) return;
        let sessionMap = state.sessionMap instanceof Map ? new Map(state.sessionMap) : new Map();
        if (!sessionMap.has(sessionId)) {
            sessionMap.set(sessionId, {
                messages: [],
                unreadCount: 0,
                lastMsg: null,
                lastMsgTime: null,
                friendInfo: friendInfo || null
            });
            commit('SET_SESSION_MAP', sessionMap);
        }
    },

    async loadMoreFriends({ dispatch, state }) {
        const { currentPage, pageSize, loading, hasMore } = state.friendsPagination;
        if (loading || !hasMore) return [];
        return await dispatch('getFriends', {
            page: currentPage + 1,
            pageSize: pageSize,
            append: true,
            resetPagination: false
        });
    },

    async resetAndLoadFriends({ dispatch }) {
        return await dispatch('getFriends', { page: 1, pageSize: 20, append: false, resetPagination: true });
    },

    async addFriend({ commit, dispatch, state }, agentInfo) {
        try {
            const newFriend = {
                agentAccountGuid: agentInfo.agentAccountGuid,
                agentName: agentInfo.agentName || '经纪人',
                agentAvatar: agentInfo.agentAvatar || Constant.DEFAULT_AVATAR,
                agentGuid: agentInfo.agentGuid,
                agentMobile: agentInfo.agentMobile,
                nickName: agentInfo.agentName || '经纪人',
                roles: agentInfo.roles || 'agent',
                lastMsg: '',
                lastTime: Date.now(),
                unreadCount: 0
            };

            const exists = state.friendslist?.some(f => f?.agentAccountGuid === newFriend.agentAccountGuid);
            if (exists) {
                await dispatch('initSession', { sessionId: agentInfo.agentAccountGuid, friendInfo: newFriend });
                return true;
            }

            const updatedFriendsList = [...(state.friendslist || []), newFriend];
            commit('SET_FRIENDS_LIST', updatedFriendsList);

            let sessionMap = state.sessionMap instanceof Map ? new Map(state.sessionMap) : new Map();
            if (!sessionMap.has(agentInfo.agentAccountGuid)) {
                sessionMap.set(agentInfo.agentAccountGuid, {
                    messages: [],
                    unreadCount: 0,
                    lastMsg: null,
                    lastMsgTime: null,
                    friendInfo: newFriend
                });
                commit('SET_SESSION_MAP', sessionMap);
            }

            try {
                EventBus.$emit('refrenshSessionList', true);
                EventBus.$emit('force-refresh-conversations', { timestamp: Date.now() });
            } catch (e) {
                // 静默失败，不影响主流程
                console.warn('[IM] 触发刷新事件失败:', e);
            }
            return true;
        } catch (error) {
            console.error('添加好友失败:', error);
            return false;
        }
    },

    async forceRefreshConversations({ commit, state }) {
        if (state.friendslist?.length) {
            const uniqueMap = new Map();
            state.friendslist.forEach(friend => {
                if (friend?.agentAccountGuid) uniqueMap.set(friend.agentAccountGuid, friend);
            });
            const uniqueFriendsList = Array.from(uniqueMap.values());
            if (uniqueFriendsList.length !== state.friendslist.length) {
                commit('SET_FRIENDS_LIST', uniqueFriendsList);
            }
        }
        commit('FORCE_REFRESH_CONVERSATIONS');
    },
};